import styled from '@emotion/styled';
import type { ReactElement, ReactNode } from 'react';

import { Pictogram, type PictogramName, StyledButton } from '@travelnest/hui';

import { Heading, Panel, Stack, Text } from '@/components/ui';
import { spacing } from '@/lib/theme';

import { PageShell } from './PageShell';
import { HostedBy, SiteHeader } from './SiteHeader';

export const TRAVELNEST_URL = 'https://travelnest.com';

const StatusPanel = styled(Panel)`
	margin-top: ${spacing.space60};
	text-align: center;
`;

/** HUI's button styling on a real link, so it works without JS and with middle-click. */
export const LinkButton = StyledButton.withComponent('a');

interface StatusPageProps {
	pictogram: PictogramName;
	title: string;
	description: string;
	/** Small print under the description, e.g. the HTTP status. */
	caption?: string;
	actions: ReactNode;
}

/**
 * The body shared by the 404 and error pages: a pictogram, a heading, a line
 * of copy and a way out. `_error.tsx` reuses it so both pages match.
 */
export const StatusPage = ({
	pictogram,
	title,
	description,
	caption,
	actions,
}: StatusPageProps) => (
	<StatusPanel padding="lg">
		<Stack gap="space50" align="center">
			<Pictogram name={pictogram} size={80} />
			<Stack gap="space20" align="center">
				<Heading as="h1" size="lg" align="center">
					{title}
				</Heading>
				<Text variant="lead" align="center">
					{description}
				</Text>
				{caption && (
					<Text variant="caption" color="muted">
						{caption}
					</Text>
				)}
			</Stack>
			{actions}
		</Stack>
	</StatusPanel>
);

/** Both status pages sit in the narrow single-column frame. */
export const statusPageLayout = (page: ReactElement) => (
	<PageShell narrow header={<SiteHeader start={<HostedBy />} />}>
		{page}
	</PageShell>
);
